import {
  BoardAction,
  BoardPhase,
  BoardPhaseUiStatus,
  BoardSection,
  BoardStep,
  BoardStepUiStatus,
  CaseParticipantType,
  CaseWorkflowView,
  ParticipantRef,
  PersonaStep,
  WorkflowBoard,
} from "../dtos";

/**
 * Pure derivation of the Nawy Now board from a persona-scoped workflow view.
 *
 * Rules (single source of truth for the tab):
 * - settled  = APPROVED | SKIPPED (hidden steps never arrive — omitted server-side)
 * - blocking = REJECTED (§Rework-not-rejected: REWORK_REQUIRED is a PARKED reviewer step, not a
 *   rejection — it reads as in-progress)
 * - actionable = access 'edit' && agent may write it && !settled && !blocked
 * - nextAction = first editable blocking step, else first actionable step, else null
 * - urgentActions = every blocking step (view-only ones render without a CTA)
 * - upcomingActions = the remaining unsettled editable steps, in step order
 *
 * §AgentAccess folds the AGENT's own permission in: a read-level (or no-access) step never
 * gets a CTA, and a write-level step that hasn't opened yet is UPCOMING, not actionable.
 * Steps from older WM deploys carry no agentAccess — they keep the pre-§AgentAccess behavior.
 *
 * §MD-bypass: a phase carrying `bypass` (an MD bypassed it) is reported as its own PhaseSignal —
 * see `phaseSignals` — and is deliberately NOT folded into `stepUiStatus` / `phaseUiStatus`. The
 * phase's steps stay exactly as open/workable as before (IN_PROGRESS / NOT_STARTED / READY as
 * usual); a bypassed phase must never read SKIPPED / "Not applicable" just because it no longer
 * blocks the application.
 */

const isSettled = (step: PersonaStep) =>
  step.status === "APPROVED" || step.status === "SKIPPED";

/** §AgentView: the step's authored AGENT audience mode is authoritative on the CRM channel —
 * 'view' means read-only HERE regardless of capability buckets (an empty WRITER bucket is open to
 * every internal user server-side, which silently out-voted the Builder's "view" configuration). */
const agentViewOnly = (step: PersonaStep) => step.clientAccess?.AGENT === "view";

/** The agent holds write on the step (capability axis only — ignores the state fold). */
const agentMayWrite = (step: PersonaStep) =>
  !agentViewOnly(step) && (!step.agentAccess || step.agentAccess.level === "write");

/** The agent may act on the step RIGHT NOW (write + opened + not settled/locked). */
const agentMayEditNow = (step: PersonaStep) =>
  !agentViewOnly(step) && (step.agentAccess ? step.agentAccess.editable : true);

/** SKIPPED without `resolved` = never applicable to this case (auto-skipped joint/guarantor
 * block or unmet condition) — settled, but NOT work that was done. SKIPPED+resolved means the
 * step WAS worked and later closed by a flow rewind, which does count as done. */
const isNotApplicable = (step: PersonaStep) =>
  step.status === "SKIPPED" && !step.resolved;

/**
 * §Rework-not-rejected: REJECTED only. `REWORK_REQUIRED` used to sit here too, and that was a
 * misreading of what the status means.
 *
 * In workflow-manager it is written to exactly ONE step, and it is not the one that owes work:
 * `WorkflowStepLifecycleService.requestRework` stamps it on the ACTING step — the reviewer's own —
 * which then sits PARKED until the chain it sent back settles and §Rework-return reactivates it. The
 * step that actually owes the work is the target, which is restarted to IN_PROGRESS and flagged
 * `editsNeeded`; that one already lands on EDITS_NEEDED here, correctly.
 *
 * So folding it in with REJECTED painted the reviewer's parked step red, and — because `phaseUiStatus`
 * blocks on ANY blocking step and `PHASE_STATUS_LABEL.BLOCKER` is the literal word "Rejected" — made a
 * whole phase tab read "Rejected" on a case where nothing was rejected (prod journey 1179833-mn1k:
 * Legal sent Credit's DBR step back, and the "Legal Review ( Compound & Unit)" tab read Rejected).
 * It also pushed a step with nothing to do on it into `urgentActions` and `nextAction`.
 *
 * Workflow-manager already treats it as in-progress on its own side — `DEFAULT_KIND_FOR_LIFECYCLE`
 * maps REWORK_REQUIRED to the IN_PROGRESS kind, so the badge WM serves for such a step is literally
 * "In Progress". This is the CRM agreeing with the server rather than inventing a second opinion.
 *
 * `isFlowClosed` had it right all along: only REJECTED closes a flow, "REWORK_REQUIRED is deliberately
 * NOT closure". That distinction just never reached this predicate.
 */
const isBlockingStatus = (step: PersonaStep) => step.status === "REJECTED";

/**
 * §Rework-not-rejected: the reviewer's own step, parked while the work it sent back is redone. It IS
 * open — the flow is live and sitting on it — so it reads as in-progress rather than falling through
 * the ladder to "To do", which would tell the agent to act on a step that is waiting on someone else.
 */
const isReworkParked = (step: PersonaStep) => step.status === "REWORK_REQUIRED";

/**
 * §FlowClosed: a REJECTED step dead-ends the application — nothing downstream will ever be worked.
 * REWORK_REQUIRED is deliberately NOT closure: that is a send-back the agent can fix, after which
 * the flow carries on, so its later steps are still genuinely upcoming.
 *
 * Read together with the case's own verdict: a cancelled lifecycle, or a status key WM already
 * labels declined/rejected/cancelled, closes the flow even when no single step carries the rejection
 * (e.g. a case cancelled outright).
 */
const isFlowClosed = (view: CaseWorkflowView, steps: PersonaStep[]): boolean => {
  if ((view.case.status ?? "").toUpperCase() === "CANCELLED") return true;
  if (/declin|reject|cancel/i.test(view.case.statusBadge?.key ?? "")) return true;
  return steps.some((step) => step.status === "REJECTED");
};

/**
 * §Opened-consistency: the step's own STATUS is the single source of truth for "has this opened?",
 * exactly like the workflow-manager step rail. Once the engine moves a step to IN_PROGRESS (or
 * REWORK_REQUIRED) it IS open and workable — a stale `blocked` (an unmet dependency left over from
 * the seed's cross-block chaining) must NOT demote it to UPCOMING. So `blocked` only decorates a
 * step that genuinely hasn't opened yet (NOT_STARTED); it never overrides an opened step.
 */
const isPending = (step: PersonaStep) =>
  step.status === "NOT_STARTED" && step.blocked;

const isActionable = (step: PersonaStep) =>
  step.access === "edit" &&
  agentMayEditNow(step) &&
  !isSettled(step) &&
  !isPending(step);

/** §EditsNeeded (design 25-Jul): the owner owes an action on reviewer feedback — reworked, or
 * holding a rejected file/value. Settled steps flag too: a reviewer rejecting a document on an
 * APPROVED Credit Documents step must pull the agent back even though the flow moved on. */
const isEditsNeeded = (step: PersonaStep) =>
  Boolean(step.editsNeeded) && !isBlockingStatus(step) && !isNotApplicable(step);

/** §Conditional: approved WITH conditions — same surfacing as EDITS_NEEDED, in orange (red wins
 * when the step also carries a rejection). */
const isConditional = (step: PersonaStep) =>
  Boolean(step.conditionalItems?.length) &&
  !isEditsNeeded(step) &&
  !isBlockingStatus(step) &&
  !isNotApplicable(step);

export const stepUiStatus = (step: PersonaStep): BoardStepUiStatus => {
  if (isBlockingStatus(step)) return "BLOCKER";
  if (isNotApplicable(step)) return "SKIPPED";
  // §EditsNeeded beats DONE and READY: rejected feedback re-opens the agent's obligation even on
  // a settled step, and a re-filled form still carries the send-back until re-submitted
  if (isEditsNeeded(step)) return "EDITS_NEEDED";
  if (isConditional(step)) return "CONDITIONAL";
  if (isSettled(step)) return "DONE";
  // §W3.4: an opened step whose form is already complete (server-computed) awaits only submit
  if (step.ready) return "READY";
  // opened → live, never UPCOMING even if a stale dependency is still unmet.
  // §Rework-not-rejected: a parked reviewer step is open too — its work is simply in flight elsewhere.
  if (step.status === "IN_PROGRESS" || isReworkParked(step)) return "IN_PROGRESS";
  if (isPending(step)) return "UPCOMING";
  return "TODO";
};

const stepSubtitle = (step: PersonaStep): string | null => {
  // a settled step's dependency chain is history — never caption it "Blocked by"
  if (isNotApplicable(step)) return "Not applicable to this case";
  if (isSettled(step)) return step.intro ?? null;
  // only a genuinely not-yet-opened step is "Blocked by" — an opened step shows its intro
  if (isPending(step) && step.blockedBy?.length) {
    return `Blocked by ${step.blockedBy.join(", ")}`;
  }
  return step.intro ?? null;
};

/**
 * §VerdictActions: kinds that exist BECAUSE a reviewer said something — a rejection, a send-back, or an
 * approval with conditions. They always have a note to read, so they always get a way in.
 *
 * This is why they cannot be gated on write access. A CONDITIONAL step is APPROVED (settled ⇒ `access: 'view'`),
 * so the write test made `hasCta` false and the "Conditional approvals" row rendered with no button at all —
 * the modal named a condition and then offered no way to reach it. A rejected step that the agent may only
 * view had the same hole. Navigating to a step and reading its verdict is never a write.
 */
const VERDICT_KINDS: BoardAction["kind"][] = ["CONDITIONAL", "BLOCKING", "EDITS"];

const toAction = (
  step: PersonaStep,
  kind: BoardAction["kind"]
): BoardAction => {
  const canWrite = step.access === "edit" && agentMayWrite(step);
  return {
    stepUid: step.uid,
    title: step.stepName,
    phaseName: step.phaseName,
    kind,
    uiStatus: stepUiStatus(step),
    subtitle: stepSubtitle(step),
    canWrite,
    // A view-only MILESTONE still has nothing to do and stays button-less; a verdict always opens.
    hasCta: canWrite || VERDICT_KINDS.includes(kind),
    ...(step.reopened ? {reopened: true} : {}),
  };
};

/** §Sections: which party divider a step files under within its phase. PRIMARY and shared (NONE)
 * steps are the applicant's own flow; SECONDARY/TERTIARY are a co-applicant's pre-seeded block,
 * keyed per participant so two joints get two sections. */
const sectionKeyOf = (step: PersonaStep): string =>
  step.party === "SECONDARY" || step.party === "TERTIARY"
    ? `${step.party}:${step.participantId ?? "?"}`
    : "APPLICANT";

const SECTION_PARTY_LABEL: Record<string, string> = {
  SECONDARY: "Joint Client",
  TERTIARY: "Guarantor",
};

const sectionLabelOf = (
  key: string,
  steps: PersonaStep[],
  participants: ParticipantRef[]
): string => {
  if (key === "APPLICANT") {
    const name = participants.find((p) => p.type === "PRIMARY")?.customer
      ?.name;
    return name ? `Applicant — ${name}` : "Applicant";
  }
  const [party] = key.split(":");
  const base = SECTION_PARTY_LABEL[party] ?? party;
  const name = participants.find(
    (p) => p.id != null && p.id === steps[0]?.participantId
  )?.customer?.name;
  return name ? `${base} — ${name}` : base;
};

/** Group a phase's visible steps into party sections, first-appearance order. */
const deriveSections = (
  steps: BoardStep[],
  participants: ParticipantRef[]
): BoardSection[] => {
  const sections: BoardSection[] = [];
  const byKey = new Map<string, BoardSection>();
  for (const boardStep of steps) {
    const key = sectionKeyOf(boardStep.step);
    let section = byKey.get(key);
    if (!section) {
      section = { key, label: "", steps: [] };
      byKey.set(key, section);
      sections.push(section);
    }
    section.steps.push(boardStep);
  }
  for (const section of sections) {
    section.label = sectionLabelOf(
      section.key,
      section.steps.map((s) => s.step),
      participants
    );
  }
  return sections;
};

/**
 * §PhaseSignals: what this phase is carrying, computed from its steps and INDEPENDENT of the
 * uiStatus ladder below. The ladder returns one value and its first rung is "every step settled
 * ⇒ DONE", so a conditional approval or a sent-back document on an already-APPROVED step never
 * reached the tab — it read a clean "Done". These are reported alongside, so the stepper can say
 * Done AND Conditional, or Current AND Reopened.
 */
const phaseSignals = (
  steps: BoardStep[],
  bypass: BoardPhase["bypass"]
) => ({
  rejected: steps.some((b) => b.uiStatus === "BLOCKER"),
  // §Rework-not-rejected: a send-back does not raise the phase's red "Edits needed" chip, for the
  // same reason its own row is no longer tinted — nothing on it was refused. The tab was
  // contradicting the step underneath it: "Edits needed" on a phase whose only flagged row read
  // "Reopened · In progress". A step actually holding a rejected file or field review still raises
  // it, and the step keeps its EDITS_NEEDED uiStatus either way, so the next-action banner and the
  // urgent list are untouched.
  editsNeeded: steps.some(
    (b) => b.uiStatus === "EDITS_NEEDED" && !isReworkOnly(b.step)
  ),
  conditional: steps.some((b) => b.uiStatus === "CONDITIONAL"),
  // §Reopened: a return visit the AGENT can still write — a reviewer's reopened step is not
  // the agent's debt, so it does not raise the signal here either.
  reopened: steps.some(
    (b) =>
      Boolean(b.step.reopened) &&
      b.step.status === "IN_PROGRESS" &&
      agentMayWrite(b.step)
  ),
  // §MD-bypass: computed straight from the phase's own `bypass` stamp — independent of every step
  // underneath it (which stay IN_PROGRESS/NOT_STARTED as usual; their uiStatus never changes for
  // this), exactly like the other §PhaseSignals here.
  bypassed: Boolean(bypass),
});

const phaseUiStatus = (
  steps: PersonaStep[],
  nextActionStepUid: string | null
): BoardPhaseUiStatus => {
  // §EditsNeeded beats DONE on the tab too (QA 2026-08-16): a settled phase holding a re-opened /
  // sent-back step is work owed NOW — a green "Done" tab hid it behind the signal chip. It falls
  // through to CURRENT (the send-back is the next action) or IN_PROGRESS instead.
  if (steps.length && steps.every(isSettled) && !steps.some(isEditsNeeded)) {
    // all settled ≠ all done: a phase whose every step was auto-skipped had no work happen
    return steps.every(isNotApplicable) ? "SKIPPED" : "DONE";
  }
  if (steps.some(isBlockingStatus)) return "BLOCKER";
  if (nextActionStepUid && steps.some((s) => s.uid === nextActionStepUid)) {
    return "CURRENT";
  }
  // §Rework-not-rejected: a phase holding a parked reviewer step is live, not UPCOMING — the flow is
  // sitting inside it waiting on the send-back, which is the same thing IN_PROGRESS says.
  if (
    steps.some(
      (s) => isSettled(s) || s.status === "IN_PROGRESS" || isReworkParked(s)
    )
  ) {
    return "IN_PROGRESS";
  }
  return "UPCOMING";
};

export const deriveBoard = (
  view: CaseWorkflowView,
  participantUid: string,
  participantType: CaseParticipantType
): WorkflowBoard => {
  const allSteps = view.phases
    .flatMap((phase) => phase.steps)
    .sort((a, b) => a.stepOrder - b.stepOrder);

  // §EditsNeeded joins the urgent surface: a sent-back step IS the agent's next action, and the
  // banner needs every such step to compose "Update KYC, credit documents & payment plan".
  const blockingSteps = allSteps.filter(
    (s) => isBlockingStatus(s) || isEditsNeeded(s) || isConditional(s)
  );
  const nextActionStep =
    blockingSteps.find((s) => s.access === "edit" && agentMayEditNow(s)) ??
    allSteps.find(isActionable) ??
    null;

  const actionKindOf = (step: PersonaStep): BoardAction["kind"] =>
    isBlockingStatus(step)
      ? "BLOCKING"
      : isEditsNeeded(step)
        ? "EDITS"
        : isConditional(step)
          ? "CONDITIONAL"
          : "NEXT";

  const nextAction = nextActionStep
    ? toAction(nextActionStep, actionKindOf(nextActionStep))
    : null;

  // §NextForAgent (product ask, 2026-08-06): with nothing agent-actionable RIGHT NOW the bar
  // still names the next step that will land on the agent — the first unsettled agent-editable
  // step in flow order, dependency-blocked or not (that's exactly why it isn't `nextAction`).
  const nextAgentStep = isFlowClosed(view, allSteps)
    ? null
    : (allSteps.find(
        (s) => s.access === "edit" && agentMayWrite(s) && !isSettled(s)
      ) ?? null);
  const nextAgentAction = nextAgentStep
    ? toAction(nextAgentStep, "NEXT")
    : null;

  const urgentActions = blockingSteps.map((s) => toAction(s, actionKindOf(s)));

  // §FlowClosed: a closed application has no upcoming work — listing "Credit Documents, DBR &
  // Payment Plan, +9" behind a "Flow blocked — I-Score rejected" banner promises steps that can
  // never run. The blocking step itself still surfaces through urgentActions.
  const upcomingActions = isFlowClosed(view, allSteps)
    ? []
    : allSteps
        .filter(
          (s) =>
            s.access === "edit" &&
            agentMayWrite(s) &&
            !isSettled(s) &&
            !isBlockingStatus(s) &&
            !isEditsNeeded(s) &&
            !isConditional(s) &&
            // §39 (QA): a dependency-blocked step is not yet anyone's action — listing it with a live
            // CTA promises work the engine will refuse. It joins the list when its prerequisites settle.
            !(s.status === "NOT_STARTED" && s.blocked) &&
            s.uid !== nextActionStep?.uid
        )
        .map((s) => toAction(s, "UPCOMING"));

  // §NA-hidden: steps that never applied to this case (auto-skipped joint/guarantor blocks,
  // unmet conditions) are noise for an agent — drop them, and drop a phase left empty by the
  // filter. Order is re-derived from the surviving phases so the stepper stays 1..n.
  const phases: BoardPhase[] = view.phases
    .map((phase) => ({
      name: phase.name,
      badge: phase.statusBadge,
      // §MD-bypass: carried through unfiltered — a bypassed phase can still hold visible steps
      // (its own steps stay open/workable), so this simply rides along with the phase row.
      bypass: phase.bypass ?? null,
      visibleSteps: phase.steps
        .filter((step) => !isNotApplicable(step))
        .sort((a, b) => a.stepOrder - b.stepOrder),
    }))
    .filter((phase) => phase.visibleSteps.length > 0)
    .map((phase, index): BoardPhase => {
      const steps = phase.visibleSteps.map(
        (step): BoardStep => ({
          step,
          uiStatus: stepUiStatus(step),
          subtitle: stepSubtitle(step),
          badge: step.statusBadge,
        })
      );
      return {
        name: phase.name,
        order: index + 1,
        uiStatus: phaseUiStatus(phase.visibleSteps, nextActionStep?.uid ?? null),
        badge: phase.badge,
        signals: phaseSignals(steps, phase.bypass),
        bypass: phase.bypass,
        // §EditsNeeded: something in this phase owes attention — the tab wears the dot
        // (edits needed / blocker / a conditional approval the agent should read)
        attention: steps.some(
          (boardStep) =>
            boardStep.uiStatus === "EDITS_NEEDED" ||
            boardStep.uiStatus === "BLOCKER" ||
            boardStep.uiStatus === "CONDITIONAL" ||
            // §Reopened: an open return visit the AGENT can write is red — its phase wears
            // the dot too (a reviewer's reopened step is not the agent's debt)
            (Boolean(boardStep.step.reopened) &&
              boardStep.step.status === "IN_PROGRESS" &&
              agentMayWrite(boardStep.step))
        ),
        steps,
        sections: deriveSections(steps, view.case.participants ?? []),
      };
    });

  // §PhaseSignals: name the phase the flow moves to next. It can only be decided once every phase
  // has a status, because it is "the first phase still to be worked AFTER the current one" — and
  // CURRENT is wherever the single next-action step happens to sit, not a fixed position. Falls
  // back to the first unworked phase when nothing is current (e.g. everything is pending others).
  const currentIndex = phases.findIndex((phase) => phase.uiStatus === "CURRENT");
  const nextIndex = phases.findIndex(
    (phase, index) => index > currentIndex && phase.uiStatus === "UPCOMING"
  );
  if (nextIndex >= 0) phases[nextIndex].uiStatus = "NEXT";

  return {
    caseUid: view.case.uid,
    caseTitle: view.case.title,
    caseStatus: view.case.status,
    statusBadge: view.case.statusBadge,
    categoryStatuses: view.case.categoryStatuses,
    onHold: view.case.onHoldAt
      ? {
          at: view.case.onHoldAt,
          by: view.case.onHoldBy ?? null,
          reason: view.case.onHoldReason ?? null,
          attachments: view.case.onHoldAttachments ?? [],
        }
      : null,
    // §Flow-closed-note: the stamp describes the LAST closure and survives a reopen — CANCELLED only.
    closure:
      (view.case.status ?? "").toUpperCase() === "CANCELLED" && view.case.closedAt
        ? {
            at: view.case.closedAt,
            by: view.case.closedBy ?? null,
            stepName: view.case.closedStepName ?? null,
            reason: view.case.closureReason ?? null,
            attachments: view.case.closureAttachments ?? [],
          }
        : null,
    agent: view.case.agent ?? null,
    participantUid,
    participantType,
    nextAction,
    nextAgentAction,
    urgentActions,
    upcomingActions,
    phases,
  };
};

// ---------------------------------------------------------------------------
// Shared presentation maps (labels used by the stepper, cards and modal)
// ---------------------------------------------------------------------------

export const STEP_STATUS_LABEL: Record<BoardStepUiStatus, string> = {
  DONE: "Done",
  IN_PROGRESS: "In Progress",
  READY: "Ready",
  EDITS_NEEDED: "Edits needed",
  CONDITIONAL: "Conditional",
  TODO: "To do",
  UPCOMING: "Upcoming",
  // design (d_iscore---Rejected): the blocked state reads "Rejected", not "Blocker"
  BLOCKER: "Rejected",
  SKIPPED: "Not applicable",
};

/**
 * §Rework-not-rejected: this step is back with the agent because the flow RETURNED it, not because
 * anything was refused — so it must not wear the rejection treatment.
 *
 * The two cases are distinguishable in the payload, and workflow-manager already separates them:
 * `editsNeededOf` builds `items` from REJECTED uploads and REJECTED field reviews, and sets the
 * `reworked` flag from an open rework loop, then returns `{}` when neither holds. So an
 * `editsNeeded` step with an EMPTY `editsNeededItems` can only be a rework send-back — there is no
 * other way to reach that combination.
 *
 * Verified on prod journey 1179833-mn1k: its reopened "Credit Review - DBR & Payment Plan" carries
 * `field_reviews = {}`, zero non-superseded REJECTED uploads and one rework comment. Nothing on it
 * was rejected; Legal simply sent it back.
 *
 * Deliberately NOT a uiStatus change. The step still owes work, so it must stay EDITS_NEEDED and
 * keep its place in `urgentActions` / `nextAction` — dropping it to IN_PROGRESS would take the
 * agent's own next action off the banner. This is presentation only.
 */
export const isReworkOnly = (step: PersonaStep): boolean =>
  Boolean(step.editsNeeded) && !step.editsNeededItems?.length;

/**
 * §Label-honesty: the ui statuses whose OWN wording beats the funnel badge workflow-manager serves.
 *
 * Everywhere else the badge should win, and deliberately so — it carries the template's authored
 * wording ("Credit Declined", "In Review") where this map only has a generic "Rejected"/"In Progress".
 * But the badge answers "where does this step sit in the funnel", and these three answer something the
 * funnel has no vocabulary for, so letting it override them loses information rather than adding it:
 *
 * - EDITS_NEEDED / CONDITIONAL are derived from rejected files, rejected field reviews and open rework
 *   loops. No funnel row describes those, so the badge silently downgrades "you owe a fix" to whatever
 *   lifecycle the step happens to be in — usually "In Progress".
 * - SKIPPED is worse than generic, it is wrong: WM maps the SKIPPED lifecycle onto the NOT_STARTED kind
 *   on purpose ("a precondition hid the step, so it never ran: to the applicant that reads as 'not
 *   started'"), so a step that will NEVER run serves the badge "Not Started" — which reads as work still
 *   to come. Verified on prod journey 1179833-mn1k: its ten auto-skipped Joint/Guarantor steps all carry
 *   `not-started / Not Started`. To an agent the truth is "Not applicable".
 */
const DERIVED_LABEL_WINS: BoardStepUiStatus[] = [
  "EDITS_NEEDED",
  "CONDITIONAL",
  "SKIPPED",
];

/**
 * §Label-honesty: what a step row calls itself — the one place the badge-vs-derived rule is spelled, so
 * the card's three render sites cannot drift apart on it.
 */
export const stepStatusText = (boardStep: {
  uiStatus: BoardStepUiStatus;
  badge?: { label: string } | null;
}): string =>
  DERIVED_LABEL_WINS.includes(boardStep.uiStatus)
    ? STEP_STATUS_LABEL[boardStep.uiStatus]
    : (boardStep.badge?.label ?? STEP_STATUS_LABEL[boardStep.uiStatus]);

export const PHASE_STATUS_LABEL: Record<BoardPhaseUiStatus, string> = {
  NEXT: "Next",
  DONE: "Done",
  CURRENT: "Current",
  IN_PROGRESS: "In Progress",
  UPCOMING: "Upcoming",
  BLOCKER: "Rejected",
  SKIPPED: "Not applicable",
};

/**
 * CTA verb heuristic for the upcoming-actions modal (mock shows Update/Upload/Create).
 *
 * §VerdictActions: a verdict the agent cannot write reads "View" — it takes them to the step to read the
 * reviewer's note, and promising "Upload" on a step that will refuse the upload is worse than no button.
 */
export const actionCtaLabel = (action: BoardAction): string => {
  if (!action.canWrite) return "View";
  if (/document|upload|kyc/i.test(action.title)) return "Upload";
  if (/plan|create/i.test(action.title)) return "Create";
  return "Update";
};
